/**
 * Danh mục mã thao tác của `audit_log.action` mà backend **thật sự ghi**
 * (ADR-0089 mục 6), gom theo nhóm dùng chung cho nhãn và bộ lọc.
 *
 * Nguồn: soát mã nguồn các service, Gateway, SPI Keycloak, module (26/10/2026).
 * Tên mã lẫn hai kiểu — `TENANT_CREATED` lẫn `tenant.contact.created` — vì mỗi
 * service đặt theo quy ước riêng; danh mục giữ nguyên văn, không chuẩn hoá.
 *
 * Mỗi nhóm lọc bằng hai thứ gửi lên audit-service: mã khớp đúng (`action=`) và
 * tiền tố (`action_prefix=`), gộp theo phép hoặc. Tiền tố bắt cả mã mới cùng họ
 * mà danh mục chưa biết; mã lạ ngoài mọi họ (ví dụ tên hàm camelCase của bản
 * ghi DENY tự động) chỉ thấy khi không lọc theo nhóm.
 *
 * Hàm thuần, không phụ thuộc React, để chạy bằng `node --test`.
 */

export type NhomHanhDong =
  | 'dang_nhap'
  | 'nguoi_dung'
  | 'tenant'
  | 'bao_mat'
  | 'license_module'
  | 'truy_cap_api'
  | 'van_hanh'
  | 'ai';

export const NHOM_HANH_DONG: readonly NhomHanhDong[] = [
  'dang_nhap',
  'nguoi_dung',
  'tenant',
  'bao_mat',
  'license_module',
  'truy_cap_api',
  'van_hanh',
  'ai',
];

/** Phương thức HTTP: Gateway, SDK và bộ lọc DENY ghi thẳng làm `action`. */
export const PHUONG_THUC_HTTP: readonly string[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

/** Điều kiện lọc của một nhóm: mã khớp đúng và tiền tố. */
export interface DieuKienNhom {
  ma: readonly string[];
  tienTo: readonly string[];
}

export const DIEU_KIEN_NHOM: Record<NhomHanhDong, DieuKienNhom> = {
  dang_nhap: { ma: [], tienTo: ['auth.'] },
  nguoi_dung: {
    ma: [
      'KEYCLOAK_INVITE_SENT',
      'KEYCLOAK_SESSIONS_REVOKED',
      'KEYCLOAK_OTP_RESET',
      'KEYCLOAK_LOGIN_UNLOCKED',
      'KEYCLOAK_PASSWORD_RESET_SENT',
      'PERMISSION_CHECK',
    ],
    tienTo: ['USER_', 'ROLE_', 'DEPARTMENT_', 'PLATFORM_STAFF_', 'PLATFORM_ROLE_', 'KEYCLOAK_USER_'],
  },
  tenant: {
    ma: [
      'TENANT_CREATED',
      'TENANT_VIEWED',
      'TENANT_UPDATED',
      'TENANT_APPROVED',
      'TENANT_SUSPENDED',
      'TENANT_RESUMED',
      'TENANT_ONBOARDING_FINISHED',
      'TENANT_ONBOARDING_REVERTED',
      'KEYCLOAK_TENANT_GROUP_CREATED',
    ],
    tienTo: ['TENANT_DOMAIN_', 'tenant.contact.'],
  },
  bao_mat: {
    ma: [
      'ip.monitor',
      'KEYCLOAK_TENANT_MFA_ENABLED',
      'KEYCLOAK_TENANT_MFA_DISABLED',
      'KEYCLOAK_TENANT_SESSIONS_REVOKED',
      'KEYCLOAK_TENANT_PASSWORD_POLICY_SYNCED',
      'TENANT_SESSIONS_REVOKED_MFA_APPLY_NOW',
    ],
    tienTo: ['security.', 'api_key.'],
  },
  license_module: { ma: [], tienTo: ['license.', 'MODULE_'] },
  truy_cap_api: { ma: PHUONG_THUC_HTTP, tienTo: [] },
  van_hanh: { ma: ['notification.sent'], tienTo: ['audit.', 'ops.'] },
  ai: { ma: ['llm.goi', 'AI_MODEL_CREATED', 'AI_MODEL_UPDATED', 'TENANT_AI_MODELS_UPDATED'], tienTo: [] },
};

/**
 * Mã đã biết của từng nhóm, theo thứ tự hiện trong ô "Chi tiết". Mỗi mã có
 * nhãn ở `nhatKy.hanhDong.<mã>`, trừ phương thức HTTP dùng chung một nhãn có
 * tham số (`nhatKy.goiApi`).
 */
export const DANH_MUC: Record<NhomHanhDong, readonly string[]> = {
  dang_nhap: ['auth.login_error', 'auth.user_temporarily_locked'],
  nguoi_dung: [
    'USER_INVITED',
    'USER_INVITE_RESENT',
    'USER_INVITE_REJECTED',
    'USER_PROFILE_UPDATED',
    'USER_DEPARTMENT_CHANGED',
    'USER_ROLES_CHANGED',
    'USER_DISABLED',
    'USER_ENABLED',
    'USER_MFA_REQUIRED_ENABLED',
    'USER_MFA_REQUIRED_DISABLED',
    'USER_PASSWORD_RESET_SENT',
    'USER_LOGIN_UNLOCKED',
    'USER_MFA_RESET',
    'USER_BRUTE_FORCE_UNLOCKED',
    'ROLE_CREATED',
    'ROLE_UPDATED',
    'ROLE_PERMISSIONS_CHANGED',
    'ROLE_DELETED',
    'DEPARTMENT_CREATED',
    'DEPARTMENT_UPDATED',
    'DEPARTMENT_DELETED',
    'PLATFORM_STAFF_REQUESTED',
    'PLATFORM_STAFF_APPROVED',
    'PLATFORM_STAFF_UPDATED',
    'PLATFORM_STAFF_ROLE_CHANGED',
    'PLATFORM_STAFF_ROLES_CHANGED',
    'PLATFORM_STAFF_DISABLED',
    'PLATFORM_STAFF_ENABLED',
    'PLATFORM_STAFF_INVITE_SENT',
    'PLATFORM_STAFF_INVITE_RESENT',
    'PLATFORM_ROLE_CREATED',
    'PLATFORM_ROLE_UPDATED',
    'PLATFORM_ROLE_PERMISSIONS_CHANGED',
    'PLATFORM_ROLE_DELETED',
    'KEYCLOAK_USER_CREATED',
    'KEYCLOAK_USER_DELETED',
    'KEYCLOAK_USER_DISABLED',
    'KEYCLOAK_USER_ENABLED',
    'KEYCLOAK_USER_BRUTE_FORCE_CLEARED',
    'KEYCLOAK_USER_MFA_REQUIRED',
    'KEYCLOAK_USER_MFA_NOT_REQUIRED',
    'KEYCLOAK_INVITE_SENT',
    'KEYCLOAK_SESSIONS_REVOKED',
    'KEYCLOAK_OTP_RESET',
    'KEYCLOAK_LOGIN_UNLOCKED',
    'KEYCLOAK_PASSWORD_RESET_SENT',
    'PERMISSION_CHECK',
  ],
  tenant: [
    'TENANT_CREATED',
    'TENANT_VIEWED',
    'TENANT_UPDATED',
    'TENANT_APPROVED',
    'TENANT_SUSPENDED',
    'TENANT_RESUMED',
    'TENANT_ONBOARDING_FINISHED',
    'TENANT_ONBOARDING_REVERTED',
    'KEYCLOAK_TENANT_GROUP_CREATED',
    'TENANT_DOMAIN_ADDED',
    'TENANT_DOMAIN_CHECKED',
    'TENANT_DOMAIN_APPROVED',
    'tenant.contact.created',
    'tenant.contact.updated',
    'tenant.contact.deleted',
  ],
  bao_mat: [
    'security.policy.updated',
    'security.ip_blocklist.added',
    'security.ip_blocklist.removed',
    'ip.monitor',
    'api_key.created',
    'api_key.revoked',
    'KEYCLOAK_TENANT_MFA_ENABLED',
    'KEYCLOAK_TENANT_MFA_DISABLED',
    'KEYCLOAK_TENANT_SESSIONS_REVOKED',
    'KEYCLOAK_TENANT_PASSWORD_POLICY_SYNCED',
    'TENANT_SESSIONS_REVOKED_MFA_APPLY_NOW',
  ],
  license_module: [
    'license.issue',
    'license.revoke',
    'license.request.create',
    'license.request.approve',
    'license.request.reject',
    'license.request.cancel',
    'license.installation.register',
    'license.download_ticket',
    'license.download',
    'license.keyring.upload',
    'license.local.license_imported',
    'license.local.license_rejected',
    'license.local.keyring_imported',
    'license.local.keyring_rejected',
    'license.local.revocation_imported',
    'license.local.revocation_rejected',
    'license.local.clock_rollback',
    'license.local.license_status',
    'MODULE_INSTALLED',
    'MODULE_UNINSTALLED',
    'MODULE_UPGRADED',
    'MODULE_ROLLED_BACK',
    'MODULE_DELETED',
  ],
  truy_cap_api: PHUONG_THUC_HTTP,
  van_hanh: [
    'audit.query',
    'audit.export',
    'audit.archive.list',
    'audit.archive.download',
    'audit.archive.completed',
    'audit.archive.partition_dropped',
    'audit.archive.failed',
    'audit.alert.triggered',
    'audit.alert.acknowledged',
    'audit.trash.deleted',
    'audit.trash.restored',
    'audit.trash.purged',
    'ops.log_level.changed',
    'notification.sent',
  ],
  ai: ['llm.goi', 'AI_MODEL_CREATED', 'AI_MODEL_UPDATED', 'TENANT_AI_MODELS_UPDATED'],
};

/** Mọi mã đã biết, trừ phương thức HTTP (có nhãn riêng). */
export const MA_CO_NHAN: readonly string[] = NHOM_HANH_DONG.filter((n) => n !== 'truy_cap_api').flatMap(
  (n) => DANH_MUC[n],
);

export function laPhuongThucHttp(ma: string): boolean {
  return PHUONG_THUC_HTTP.includes(ma);
}

/** Một mã có khớp tập mã và tiền tố không; cả hai rỗng là khớp mọi mã. */
export function khopLoc(ma: string, maLoc: readonly string[], tienTo: readonly string[]): boolean {
  if (maLoc.length === 0 && tienTo.length === 0) return true;
  return maLoc.includes(ma) || tienTo.some((p) => ma.startsWith(p));
}

/** Nhóm của một mã; mã lạ ngoài mọi họ trả `null`. */
export function nhomCuaMa(ma: string): NhomHanhDong | null {
  // Khớp đúng trước: `TENANT_SESSIONS_REVOKED_MFA_APPLY_NOW` là bảo mật, dù cùng họ `TENANT_` với nhóm tenant.
  for (const n of NHOM_HANH_DONG) if (DIEU_KIEN_NHOM[n].ma.includes(ma)) return n;
  for (const n of NHOM_HANH_DONG) if (DIEU_KIEN_NHOM[n].tienTo.some((p) => ma.startsWith(p))) return n;
  return null;
}

/** Tham số `action`, `action_prefix` gửi lên audit-service; mảng rỗng là không lọc theo thao tác. */
export interface ThamSoThaoTac {
  actions: string[];
  action_prefixes: string[];
}

/**
 * Gộp nhóm đã bấm và mã đã chọn ở ô "Chi tiết" thành tham số lọc. Mã chi tiết
 * đã nằm trong một nhóm đang bấm thì bỏ, cho URL gọn; thứ tự ổn định để khoá
 * truy vấn không đổi khi người dùng chỉ bấm lại cùng tập.
 */
export function thamSoLocThaoTac(nhom: readonly NhomHanhDong[], chiTiet: readonly string[]): ThamSoThaoTac {
  const chon = NHOM_HANH_DONG.filter((n) => nhom.includes(n));
  const maNhom = chon.flatMap((n) => DIEU_KIEN_NHOM[n].ma);
  const tienTo = chon.flatMap((n) => DIEU_KIEN_NHOM[n].tienTo);
  const coNhom = chon.length > 0;
  const them = [...chiTiet].sort().filter((ma) => !coNhom || !khopLoc(ma, maNhom, tienTo));
  return {
    actions: [...new Set([...maNhom, ...them])],
    action_prefixes: [...new Set(tienTo)],
  };
}

/** Lọc danh sách mã của ô "Chi tiết" theo chuỗi tìm (khớp mã hoặc nhãn, không phân biệt hoa thường, bỏ dấu). */
export function timMa(ds: readonly string[], tim: string, nhan: (ma: string) => string): string[] {
  const q = boDau(tim.trim());
  if (!q) return [...ds];
  return ds.filter((ma) => boDau(ma).includes(q) || boDau(nhan(ma)).includes(q));
}

function boDau(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
}
